import { ReactNode } from 'react';

import { Stack } from '@mui/material';

import Checkbox from '../../checkbox/Checkbox';
import type { EnhancedItem } from '../hooks/useEnhancedItems';
import { MenuItem } from '../SharedStyledComponents';
import type { DownshiftItemProps } from '../types';

type RegularItemProps<Item> = {
  enhancedItem: EnhancedItem<Item> & { itemType: 'regular' };
  isHighlighted: boolean;
  showCheckbox: boolean;
  index: number;
  customRenderMenuItem?: (params: {
    value: string;
    item: Item;
    isSelected: boolean;
    isDisabled: boolean;
    isHighlighted: boolean;
  }) => ReactNode;
  itemProps: DownshiftItemProps;
};

/**
 * Component for rendering a regular menu item
 */
export const RegularItem = <Item,>({
  enhancedItem,
  isHighlighted,
  showCheckbox,
  customRenderMenuItem,
  itemProps,
  index,
}: RegularItemProps<Item>) => {
  const renderItemContent = (): ReactNode => {
    if (customRenderMenuItem) {
      return customRenderMenuItem({
        value: enhancedItem.value,
        item: enhancedItem.item,
        isSelected: enhancedItem.isSelected,
        isDisabled: enhancedItem.isDisabled,
        isHighlighted,
      });
    }
    return enhancedItem.label;
  };

  const isItemGroupingEnabled = enhancedItem.groupLabel !== undefined;

  return (
    <MenuItem
      data-testid={`option-item-${index}`}
      data-group-name={enhancedItem.groupLabel}
      data-selected={enhancedItem.isSelected}
      isDisabled={enhancedItem.isDisabled}
      isHighlighted={isHighlighted}
      isSelected={enhancedItem.isSelected}
      isIndented={isItemGroupingEnabled}
      {...itemProps}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap="8px"
        width="100%"
        sx={{ overflowWrap: 'anywhere' }}
      >
        {showCheckbox && (
          <Checkbox
            checked={enhancedItem.isSelected}
            disabled={enhancedItem.isDisabled}
            testId="checkbox"
          />
        )}
        {renderItemContent()}
      </Stack>
    </MenuItem>
  );
};
